import React, { useState } from 'react';
import axios from 'axios';
import { CONTACT_API_URL as API_URL } from './lib/api-config';
import AlertMessage from './components/ui/AlertMessage';

const api = axios.create({ withCredentials: true });

// HubSpot field mapping
const HUBSPOT_FIELD_MAP = {
  'First Name': 'firstName',
  'Last Name': 'lastName',
  'Email': 'email',
  'Phone Number': 'phone',
  'Phone': 'phone',
  'Company Name': 'company',
  'Company': 'company',
  'Job Title': 'role',
  'Lead Status': 'status',
  'Notes': 'notes'
};

function ContactImport() {
  const [step, setStep] = useState(1); // 1: upload, 2: map/preview, 3: results
  const [fileData, setFileData] = useState(null);
  const [parsedData, setParsedData] = useState([]);
  const [headers, setHeaders] = useState([]);
  const [fieldMapping, setFieldMapping] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [importResults, setImportResults] = useState(null);

  // Options
  const [skipDuplicates, setSkipDuplicates] = useState(true);
  const [updateExisting, setUpdateExisting] = useState(false);

  const parseCSV = (text) => {
    const lines = text.trim().split('\n');
    if (lines.length < 2) {
      throw new Error('CSV-filen verkar vara tom eller har inga datarader');
    }

    // Parse headers (first line)
    const headerLine = lines[0];
    const csvHeaders = parseCSVLine(headerLine);
    setHeaders(csvHeaders);

    // Auto-map fields based on HubSpot naming
    const autoMapping = {};
    csvHeaders.forEach((header, index) => {
      const cleanHeader = header.trim();
      if (HUBSPOT_FIELD_MAP[cleanHeader]) {
        autoMapping[index] = HUBSPOT_FIELD_MAP[cleanHeader];
      } else {
        // Try lowercase match
        const lowerHeader = cleanHeader.toLowerCase();
        if (lowerHeader === 'email' || lowerHeader === 'e-post') {
          autoMapping[index] = 'email';
        } else if (lowerHeader === 'firstname' || lowerHeader === 'first name' || lowerHeader === 'fornamn' || lowerHeader === 'förnamn') {
          autoMapping[index] = 'firstName';
        } else if (lowerHeader === 'lastname' || lowerHeader === 'last name' || lowerHeader === 'efternamn') {
          autoMapping[index] = 'lastName';
        } else if (lowerHeader === 'phone' || lowerHeader === 'telefon' || lowerHeader === 'mobil') {
          autoMapping[index] = 'phone';
        } else if (lowerHeader === 'company' || lowerHeader === 'företag' || lowerHeader === 'organization') {
          autoMapping[index] = 'company';
        }
      }
    });
    setFieldMapping(autoMapping);

    // Parse data rows
    const data = [];
    for (let i = 1; i < lines.length; i++) {
      const line = lines[i].trim();
      if (!line) continue;

      const values = parseCSVLine(line);
      const row = {};
      csvHeaders.forEach((header, index) => {
        row[header] = values[index] || '';
      });
      data.push(row);
    }

    return data;
  };

  // Parse a single CSV line, handling quoted fields
  const parseCSVLine = (line) => {
    const result = [];
    let current = '';
    let inQuotes = false;

    for (let i = 0; i < line.length; i++) {
      const char = line[i];

      if (char === '"') {
        if (inQuotes && line[i + 1] === '"') {
          current += '"';
          i++;
        } else {
          inQuotes = !inQuotes;
        }
      } else if ((char === ',' || char === ';') && !inQuotes) {
        result.push(current.trim());
        current = '';
      } else {
        current += char;
      }
    }
    result.push(current.trim());

    return result;
  };

  const parseJSON = (text) => {
    const data = JSON.parse(text);
    if (Array.isArray(data)) {
      return data;
    } else if (data.contacts && Array.isArray(data.contacts)) {
      return data.contacts;
    } else {
      throw new Error('JSON-filen måste innehålla en array eller ett objekt med "contacts"-array');
    }
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setError('');
    setFileData(file);

    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const text = event.target.result;
        let data;

        if (file.name.endsWith('.json')) {
          data = parseJSON(text);
          // For JSON, set headers from object keys
          if (data.length > 0) {
            const jsonHeaders = Object.keys(data[0]);
            setHeaders(jsonHeaders);
            // Auto-map for JSON
            const autoMapping = {};
            jsonHeaders.forEach((key, index) => {
              const lowerKey = key.toLowerCase();
              if (lowerKey === 'email' || lowerKey === 'e-post') autoMapping[index] = 'email';
              else if (lowerKey === 'firstname' || lowerKey === 'förnamn') autoMapping[index] = 'firstName';
              else if (lowerKey === 'lastname' || lowerKey === 'efternamn') autoMapping[index] = 'lastName';
              else if (lowerKey === 'phone' || lowerKey === 'telefon') autoMapping[index] = 'phone';
              else if (lowerKey === 'company' || lowerKey === 'företag') autoMapping[index] = 'company';
              else if (lowerKey === 'notes' || lowerKey === 'noteringar') autoMapping[index] = 'notes';
            });
            setFieldMapping(autoMapping);
          }
        } else {
          data = parseCSV(text);
        }

        setParsedData(data);
        setStep(2);
      } catch (err) {
        setError('Kunde inte läsa filen: ' + err.message);
      }
    };
    reader.readAsText(file);
  };

  const updateMapping = (headerIndex, field) => {
    setFieldMapping(prev => ({
      ...prev,
      [headerIndex]: field || undefined
    }));
  };

  const getMappedData = () => {
    return parsedData.map(row => {
      const mapped = {};
      headers.forEach((header, index) => {
        const targetField = fieldMapping[index];
        if (targetField) {
          mapped[targetField] = row[header];
        }
      });
      return mapped;
    });
  };

  const handleImport = async () => {
    const mappedData = getMappedData();

    // Validate that we have email mapped
    const hasEmail = Object.values(fieldMapping).includes('email');
    if (!hasEmail) {
      setError('E-post måste mappas till ett fält');
      return;
    }

    // Filter out rows without email
    const validData = mappedData.filter(row => row.email && row.email.trim());

    if (validData.length === 0) {
      setError('Inga giltiga rader att importera (alla saknar e-post)');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await api.post(`${API_URL}/contacts/import`, {
        contacts: validData,
        options: {
          skipDuplicates,
          updateExisting
        }
      });

      setImportResults(response.data.data);
      setStep(3);
    } catch (err) {
      console.error('Import error:', err);
      setError(err.response?.data?.message || 'Kunde inte importera kontakter');
    } finally {
      setLoading(false);
    }
  };

  const reset = () => {
    setStep(1);
    setFileData(null);
    setParsedData([]);
    setHeaders([]);
    setFieldMapping({});
    setImportResults(null);
    setError('');
  };

  return (
    <main className="main-content">
      <div className="page-header">
        <h2>Importera kontakter</h2>
        <p className="page-description">
          Importera leads från HubSpot eller annan källa via CSV- eller JSON-fil.
        </p>
      </div>

      <AlertMessage error={error} />

      {step === 1 && (
        <div className="card">
          <h3>Steg 1: Ladda upp fil</h3>
          <p style={{ color: '#666', marginBottom: '20px' }}>
            Ladda upp en CSV-fil (från HubSpot export) eller en JSON-fil med kontakter.
          </p>

          <div className="upload-area">
            <input
              type="file"
              accept=".csv,.json"
              onChange={handleFileUpload}
              id="file-upload"
              style={{ display: 'none' }}
            />
            <label htmlFor="file-upload" className="upload-label">
              <div className="upload-icon">+</div>
              <p>Klicka för att välja fil</p>
              <p className="upload-hint">CSV eller JSON, max 500 kontakter</p>
            </label>
          </div>

          <div className="info-box" style={{ marginTop: '30px' }}>
            <h4>Förväntade fält i HubSpot CSV:</h4>
            <ul style={{ marginLeft: '20px', color: '#666' }}>
              <li><strong>First Name</strong> - Förnamn</li>
              <li><strong>Last Name</strong> - Efternamn</li>
              <li><strong>Email</strong> - E-post (obligatoriskt)</li>
              <li><strong>Phone Number</strong> - Telefon</li>
              <li><strong>Company Name</strong> - Företag</li>
            </ul>
          </div>
        </div>
      )}

      {step === 2 && (
        <div className="card">
          <h3>Steg 2: Fältmappning och förhandsgranskning</h3>

          <div className="mapping-section">
            <h4>Fältmappning</h4>
            <p style={{ color: '#666', marginBottom: '15px' }}>
              Matcha kolumner i din fil med KursAdmin-fält. E-post är obligatoriskt.
            </p>

            <div className="mapping-grid">
              {headers.map((header, index) => (
                <div key={index} className="mapping-row">
                  <span className="source-field">{header}</span>
                  <span className="arrow">→</span>
                  <select
                    value={fieldMapping[index] || ''}
                    onChange={(e) => updateMapping(index, e.target.value)}
                  >
                    <option value="">-- Ignorera --</option>
                    <option value="firstName">Förnamn</option>
                    <option value="lastName">Efternamn</option>
                    <option value="email">E-post *</option>
                    <option value="phone">Telefon</option>
                    <option value="company">Företag</option>
                    <option value="notes">Anteckningar</option>
                  </select>
                </div>
              ))}
            </div>
          </div>

          <div className="options-section" style={{ marginTop: '30px' }}>
            <h4>Importalternativ</h4>
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={skipDuplicates}
                onChange={(e) => {
                  setSkipDuplicates(e.target.checked);
                  if (e.target.checked) setUpdateExisting(false);
                }}
              />
              <span>Hoppa över om e-post redan finns</span>
            </label>
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={updateExisting}
                onChange={(e) => {
                  setUpdateExisting(e.target.checked);
                  if (e.target.checked) setSkipDuplicates(false);
                }}
              />
              <span>Uppdatera befintliga kontakter</span>
            </label>
          </div>

          <div className="preview-section" style={{ marginTop: '30px' }}>
            <h4>Förhandsgranskning ({parsedData.length} rader)</h4>
            <div className="preview-table-wrapper">
              <table className="preview-table">
                <thead>
                  <tr>
                    <th>#</th>
                    {Object.values(fieldMapping).filter(Boolean).map((field, i) => (
                      <th key={i}>{field}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {getMappedData().slice(0, 10).map((row, i) => (
                    <tr key={i}>
                      <td>{i + 1}</td>
                      {Object.values(fieldMapping).filter(Boolean).map((field, j) => (
                        <td key={j}>{row[field] || '-'}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
              {parsedData.length > 10 && (
                <p style={{ color: '#666', marginTop: '10px' }}>
                  ... och {parsedData.length - 10} till
                </p>
              )}
            </div>
          </div>

          <div className="actions" style={{ marginTop: '30px', display: 'flex', gap: '15px' }}>
            <button className="btn btn-secondary" onClick={reset}>
              Avbryt
            </button>
            <button
              className="btn btn-primary"
              onClick={handleImport}
              disabled={loading || !Object.values(fieldMapping).includes('email')}
            >
              {loading ? 'Importerar...' : `Importera ${parsedData.length} kontakter`}
            </button>
          </div>
        </div>
      )}

      {step === 3 && importResults && (
        <div className="card">
          <h3>Steg 3: Importresultat</h3>

          <div className="results-summary">
            <div className="result-item success">
              <span className="result-number">{importResults.imported}</span>
              <span className="result-label">Importerade</span>
            </div>
            {importResults.updated > 0 && (
              <div className="result-item updated">
                <span className="result-number">{importResults.updated}</span>
                <span className="result-label">Uppdaterade</span>
              </div>
            )}
            {importResults.skipped > 0 && (
              <div className="result-item skipped">
                <span className="result-number">{importResults.skipped}</span>
                <span className="result-label">Skippade (dubbletter)</span>
              </div>
            )}
            {importResults.errors.length > 0 && (
              <div className="result-item errors">
                <span className="result-number">{importResults.errors.length}</span>
                <span className="result-label">Fel</span>
              </div>
            )}
          </div>

          {importResults.errors.length > 0 && (
            <div className="errors-section" style={{ marginTop: '30px' }}>
              <h4>Fel vid import:</h4>
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Rad</th>
                    <th>Fel</th>
                    <th>Data</th>
                  </tr>
                </thead>
                <tbody>
                  {importResults.errors.slice(0, 20).map((err, i) => (
                    <tr key={i}>
                      <td>{err.row}</td>
                      <td style={{ color: '#c62828' }}>{err.error}</td>
                      <td style={{ fontSize: '0.85em', color: '#666' }}>
                        {err.data?.email || JSON.stringify(err.data).substring(0, 50)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {importResults.errors.length > 20 && (
                <p style={{ color: '#666', marginTop: '10px' }}>
                  ... och {importResults.errors.length - 20} till
                </p>
              )}
            </div>
          )}

          <div className="actions" style={{ marginTop: '30px' }}>
            <button className="btn btn-primary" onClick={reset}>
              Importera fler kontakter
            </button>
            <a href="/contacts" className="btn btn-secondary" style={{ marginLeft: '15px' }}>
              Visa kontakter
            </a>
          </div>
        </div>
      )}

      <style>{`
        .upload-area {
          border: 2px dashed #ccc;
          border-radius: 8px;
          padding: 40px;
          text-align: center;
          transition: all 0.2s;
        }
        .upload-area:hover {
          border-color: #2ea3f2;
          background: #f8fbff;
        }
        .upload-label {
          cursor: pointer;
          display: block;
        }
        .upload-icon {
          width: 60px;
          height: 60px;
          background: #e3f2fd;
          color: #2ea3f2;
          border-radius: 50%;
          font-size: 30px;
          line-height: 60px;
          margin: 0 auto 15px;
        }
        .upload-hint {
          color: #999;
          font-size: 0.9em;
        }
        .info-box {
          background: #f5f5f5;
          padding: 20px;
          border-radius: 5px;
          border-left: 4px solid #2ea3f2;
        }
        .info-box h4 {
          margin-top: 0;
          color: #333;
        }
        .mapping-grid {
          display: flex;
          flex-direction: column;
          gap: 10px;
        }
        .mapping-row {
          display: flex;
          align-items: center;
          gap: 15px;
        }
        .source-field {
          min-width: 200px;
          padding: 8px 12px;
          background: #f5f5f5;
          border-radius: 4px;
          font-weight: 500;
        }
        .arrow {
          color: #999;
        }
        .mapping-row select {
          padding: 8px 12px;
          border: 1px solid #ccc;
          border-radius: 4px;
          min-width: 200px;
        }
        .checkbox-label {
          display: flex;
          align-items: center;
          gap: 10px;
          margin-bottom: 10px;
          cursor: pointer;
        }
        .checkbox-label input {
          width: 18px;
          height: 18px;
        }
        .preview-table-wrapper {
          overflow-x: auto;
          max-height: 400px;
          border: 1px solid #e0e0e0;
          border-radius: 5px;
        }
        .preview-table {
          width: 100%;
          border-collapse: collapse;
          font-size: 0.9em;
        }
        .preview-table th, .preview-table td {
          padding: 10px;
          text-align: left;
          border-bottom: 1px solid #e0e0e0;
        }
        .preview-table th {
          background: #f5f5f5;
          position: sticky;
          top: 0;
        }
        .results-summary {
          display: flex;
          gap: 20px;
          flex-wrap: wrap;
        }
        .result-item {
          padding: 20px 30px;
          border-radius: 8px;
          text-align: center;
        }
        .result-item.success {
          background: #e8f5e9;
          border: 1px solid #a5d6a7;
        }
        .result-item.updated {
          background: #e3f2fd;
          border: 1px solid #90caf9;
        }
        .result-item.skipped {
          background: #fff3e0;
          border: 1px solid #ffcc80;
        }
        .result-item.errors {
          background: #ffebee;
          border: 1px solid #ef9a9a;
        }
        .result-number {
          display: block;
          font-size: 2rem;
          font-weight: 700;
        }
        .result-item.success .result-number { color: #2e7d32; }
        .result-item.updated .result-number { color: #1565c0; }
        .result-item.skipped .result-number { color: #ef6c00; }
        .result-item.errors .result-number { color: #c62828; }
        .result-label {
          color: #666;
          font-size: 0.9em;
        }
        .alert {
          padding: 12px 15px;
          border-radius: 5px;
          margin-bottom: 20px;
        }
        .alert-error {
          background: #ffebee;
          color: #c62828;
          border: 1px solid #ef9a9a;
        }
        .btn-secondary {
          background: white;
          color: #666;
          border: 1px solid #ccc;
        }
        .btn-secondary:hover {
          background: #f5f5f5;
        }
      `}</style>
    </main>
  );
}

export default ContactImport;
